import { computed, Signal } from '@angular/core';

/** Configuration for building host CSS classes Supports all common form control states and variants */
export interface ClassBuilderConfig {
  baseClass: string;
  customClass?: Signal<string>;
  disabled?: Signal<boolean>;
  readonly?: Signal<boolean>;
  error?: Signal<boolean>;
  active?: Signal<boolean>;
  loading?: Signal<boolean>;
  size?: Signal<'sm' | 'md' | 'lg' | 'xl'>;
  variant?: Signal<'fill' | 'outline'>;
  [key: string]: any;
}

/**
 * Builds host CSS class string from standard form control state Follows BEM naming convention: hx-componentname--state
 *
 * Automatically handles:
 *
 * - Base class
 * - Custom CSS class
 * - State modifiers (disabled, readonly, error, active, loading)
 * - Size variants (sm, md, lg, xl)
 * - Visual variants (fill, outline)
 *
 * @example
 *   ```typescript
 *   // Build class string reactively
 *   readonly hostClasses = buildHostClasses({
 *     baseClass: 'hx-input',
 *     customClass: this.customCssClass,
 *     disabled: this.disabled,
 *     readonly: this.readonly,
 *     error: this.error,
 *     size: this.size,
 *     variant: this.variant,
 *   });
 *
 *   // In template: [class]="hostClasses()"
 *   // Output: "hx-input hx-input--md hx-input--fill hx-input--disabled"
 *   ```;
 *
 * @param config - Class building configuration
 * @returns Signal that emits updated class string
 */
export function buildHostClasses(config: ClassBuilderConfig): Signal<string> {
  return computed(() => {
    const classes: string[] = [config.baseClass];

    // Add custom class if provided
    if (config.customClass?.()) {
      classes.push(config.customClass());
    }

    // Add state modifiers
    if (config.disabled?.()) {
      classes.push(`${config.baseClass}--disabled`);
    }

    if (config.readonly?.()) {
      classes.push(`${config.baseClass}--readonly`);
    }

    if (config.error?.()) {
      classes.push(`${config.baseClass}--error`);
    }

    if (config.active?.()) {
      classes.push(`${config.baseClass}--active`);
    }

    if (config.loading?.()) {
      classes.push(`${config.baseClass}--loading`);
    }

    // Add size variant
    if (config.size?.()) {
      classes.push(`${config.baseClass}--${config.size()}`);
    }

    // Add visual variant
    if (config.variant?.()) {
      classes.push(`${config.baseClass}--${config.variant()}`);
    }

    // Filter out empty strings and join
    return classes.filter(Boolean).join(' ');
  });
}
